import React, { useRef, useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Box,
  Text,
  VStack,
  HStack,
  IconButton,
  useToast,
  Spinner,
  Image,
  Icon,
} from '@chakra-ui/react';
import { FiDownload, FiShare2, FiRefreshCw, FiCopy } from 'react-icons/fi';
import html2canvas from 'html2canvas';

// Función auxiliar para quitar tags HTML del texto
function stripHtml(html) {
  const tmp = document.createElement('DIV');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}

export default function ShareCardModal({ isOpen, onClose, text, reference }) {
  const cardRef = useRef(null);
  const [bgIndex, setBgIndex] = useState(1);
  const [bgBase64, setBgBase64] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const toast = useToast();
  const [cachedImage, setCachedImage] = useState(null);

  // Limpiar el texto si viene con HTML (como en los devocionales)
  const cleanText = text ? stripHtml(text) : '';

  // Calcular tamaño de fuente dinámico basado en la longitud del texto
  const textLength = cleanText.length;
  let textFontSize = "6.5cqi";
  let refFontSize = "4.5cqi";

  if (textLength > 100) {
    textFontSize = "5.5cqi";
    refFontSize = "4.2cqi";
  }
  if (textLength > 180) {
    textFontSize = "4.5cqi";
    refFontSize = "3.8cqi";
  }
  if (textLength > 280) {
    textFontSize = "3.8cqi";
    refFontSize = "3.2cqi";
  }
  if (textLength > 400) {
    textFontSize = "3cqi";
    refFontSize = "2.8cqi";
  }
  if (textLength > 600) {
    textFontSize = "2.5cqi";
    refFontSize = "2.2cqi";
  }

  useEffect(() => {
    if (isOpen) {
      // Fondo aleatorio al abrir (1 al 45)
      setBgIndex(Math.floor(Math.random() * 45) + 1);
    }
  }, [isOpen]);

  // Pre-cargar la imagen en Base64 para que Safari no falle al exportar
  useEffect(() => {
    const loadBg = async () => {
      try {
        const res = await fetch(`/images/backgrounds/${bgIndex}.png`);
        const blob = await res.blob();
        const reader = new FileReader();
        reader.onloadend = () => setBgBase64(reader.result);
        reader.readAsDataURL(blob);
      } catch (err) {
        console.error('Error preload bg', err);
      }
    };
    if (isOpen) {
      loadBg();
    } else {
      setCachedImage(null);
    }
  }, [bgIndex, isOpen]);

  useEffect(() => {
    let alive = true;
    if (isOpen && bgBase64 && cardRef.current) {
      setTimeout(async () => {
        try {
          const canvas = await html2canvas(cardRef.current, {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            backgroundColor: null
          });
          const dataUrl = canvas.toDataURL('image/jpeg', 0.95);
          if (alive) setCachedImage(dataUrl);
        } catch (e) {
          console.error('Error en pre-generacion', e);
        }
      }, 500); // give the modal 500ms to animate in
    }
    return () => { alive = false; };
  }, [isOpen, bgBase64]);

  const changeBackground = () => {
    let next = bgIndex + 1;
    if (next > 45) next = 1;
    setBgIndex(next);
    setCachedImage(null);
  };

  const generateImage = async () => {
    if (!cardRef.current) return null;
    
    if (cachedImage) return cachedImage;

    setIsGenerating(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: null
      });
      const dataUrl = canvas.toDataURL('image/jpeg', 0.95);
      setCachedImage(dataUrl);
      return dataUrl;
    } catch (err) {
      console.error('Error generating image', err);
      toast({
        title: 'Error',
        description: 'No se pudo generar la imagen.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return null;
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = async () => {
    const dataUrl = await generateImage();
    if (dataUrl) {
      try {
        const blob = await (await fetch(dataUrl)).blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = `SalaEvangelica-${reference ? reference.replace(/\s+/g, '-') : 'Compartir'}.jpg`;
        link.href = blobUrl;
        link.click();
        
        // Clean up memory
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      } catch (err) {
        console.error('Error downloading image', err);
      }
    }
  };

  const handleShare = async () => {
    const dataUrl = await generateImage();
    if (!dataUrl) return;

    try {
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], 'compartir.jpg', { type: 'image/jpeg' });

      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: reference || 'Sala Evangélica',
          text: 'Te comparto esta palabra de bendición.',
        });
      } else {
        // Fallback: Si no soporta Web Share API con archivos, intentar compartir texto, o avisar.
        if (navigator.share) {
          await navigator.share({
            title: reference || 'Sala Evangélica',
            text: `"${cleanText}" - ${reference}\n\nhttps://salaevangelica.com`,
          });
        } else {
          toast({
            title: 'No soportado',
            description: 'Tu dispositivo no soporta compartir directamente. Descarga la imagen en su lugar.',
            status: 'info',
            duration: 4000,
            isClosable: true,
          });
        }
      }
    } catch (err) {
      console.error('Error compartiendo', err);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay backdropFilter="blur(5px)" />
      <ModalContent bg="bg.card" borderRadius="2xl" overflow="hidden">
        <ModalHeader>Compartir</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <Text fontSize="sm" color="text.muted" textAlign="center">
              Previsualización de tu imagen. Usa el botón de refrescar para cambiar el fondo.
            </Text>

            {/* Este es el contenedor que se convertirá en imagen */}
            <Box
              ref={cardRef}
              w="100%"
              maxW="500px" // Aumentado ligeramente para mejor lectura en desktop
              mx="auto"
              sx={{ aspectRatio: '4/5', containerType: 'inline-size' }}
              position="relative"
              overflow="hidden"
              borderRadius="lg"
              boxShadow="xl"
            >
              {/* Imagen de Fondo: Usando base64 pre-cargado para 100% de éxito en html-to-image */}
              {bgBase64 && (
                <Image
                  position="absolute"
                  inset={0}
                  w="100%"
                  h="100%"
                  objectFit="cover"
                  src={bgBase64}
                  alt="Fondo"
                  crossOrigin="anonymous"
                  zIndex={0}
                />
              )}
              
              {/* Gradiente Oscuro para legibilidad */}
              <Box
                position="absolute"
                inset={0}
                bgGradient="linear(to-b, blackAlpha.600, blackAlpha.900)"
                zIndex={1}
              />

              {/* Contenido (Texto) */}
              <Box
                position="absolute"
                inset={0}
                zIndex={2}
                display="flex"
                flexDirection="column"
                justifyContent="center"
                p="8cqi"
                pb="24cqi" // Espacio extra para que no pise el footer
                color="white"
                textAlign="center"
              >
                <Text
                  color="white"
                  fontSize={textFontSize}
                  fontWeight="semibold"
                  lineHeight="1.5"
                  textShadow="0px 2px 4px rgba(0,0,0,0.8)"
                  pb="1cqi"
                  noOfLines={14} // Límite máximo de seguridad para textos súper largos
                >
                  "{cleanText}"
                </Text>
                
                {reference && (
                  <Text
                    mt="4cqi"
                    fontSize={refFontSize}
                    fontWeight="bold"
                    color="accent.300"
                    textShadow="0px 1px 3px rgba(0,0,0,0.8)"
                    pb="1cqi"
                  >
                    — {reference}
                  </Text>
                )}
              </Box>

              {/* Logo / Footer de la Iglesia */}
              <Box
                position="absolute"
                bottom="5cqi"
                left={0}
                right={0}
                zIndex={2}
                textAlign="center"
              >
                <VStack justify="center" spacing={0} opacity={0.9}>
                  <Text fontSize="3.5cqi" fontWeight="bold" letterSpacing="widest" color="white" textShadow="0px 1px 2px rgba(0,0,0,0.9)">
                    SALA EVANGÉLICA BARRIO LEMPIRA
                  </Text>
                  <Text fontSize="2.8cqi" color="whiteAlpha.800" textShadow="0px 1px 2px rgba(0,0,0,0.9)">
                    www.salaevangelica.com
                  </Text>
                </VStack>
              </Box>
            </Box>

            <Button size="sm" variant="ghost" leftIcon={<FiRefreshCw />} onClick={changeBackground}>
              Cambiar Fondo
            </Button>
          </VStack>
        </ModalBody>

        <ModalFooter display="flex" flexDir={{ base: 'column', sm: 'row' }} gap={3}>
          <Button
            w="full"
            colorScheme="blue"
            variant="outline"
            leftIcon={isGenerating ? <Spinner size="sm" /> : <FiDownload />}
            onClick={handleDownload}
            isDisabled={isGenerating}
          >
            Descargar
          </Button>
          <Button
            w="full"
            colorScheme="blue"
            variant="solid"
            leftIcon={isGenerating ? <Spinner size="sm" /> : <FiShare2 />}
            onClick={handleShare}
            isDisabled={isGenerating}
          >
            WhatsApp / Redes
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
